import {useCallback,useEffect,useRef,useState} from "react";
import { FilterValue } from "../components/types/types";

export const PRICE_MIN = 0;
export const PRICE_MAX = 100;
export const PRICE_STEP = 10;

type PriceSide = "min" | "max";

interface UsePriceSliderParams {
    initialMin?: number;
    initialMax?: number;

    onPriceChange?: (
        min: number,
        max: number
    ) => void;

    applyDelay?: number;
    showCheckAnimation?: boolean;
}
export const isPriceFilter = (value: FilterValue): value is [number, number] => {
    return (Array.isArray(value) &&value.length === 2 &&value.every(item => typeof item === "number")
)};
export const usePriceSlider = ({
    initialMin = PRICE_MIN,
    initialMax = PRICE_MAX,
    onPriceChange,
    applyDelay = 650,
    showCheckAnimation = true
}: UsePriceSliderParams = {}) => {

    const [minValue, setMinValue] = useState(initialMin);
    const [maxValue, setMaxValue] = useState(initialMax);
    const [showCheck, setShowCheck] = useState(false);
    const priceWrapperRef = useRef<HTMLDivElement | null>(null);
    const touchStartRef = useRef<{x: number;y: number;} | null>(null);

    const hasDraggedRef = useRef(false);

    const touchDirectionRef = useRef<"horizontal" | "vertical" | null>(null);

    const applyTimerRef = useRef<number | null>(null);
    const checkTimerRef = useRef<number | null>(null);
    const middle = (minValue + maxValue) / 2;

    useEffect(() => {
        setMinValue(initialMin);
        setMaxValue(initialMax);
    }, [initialMin, initialMax]);

    const triggerCheckAnimation =
        useCallback(() => {
            if (!showCheckAnimation) return;

            setShowCheck(true);

            if (checkTimerRef.current) {
                window.clearTimeout(
                    checkTimerRef.current
                );
            }

            checkTimerRef.current =
                window.setTimeout(() => {
                    setShowCheck(false);
                    checkTimerRef.current = null;
                }, 1500);
        }, [showCheckAnimation]);

    const applyPrice = useCallback((nextMin: number,nextMax: number,delay = applyDelay ) => {
            if (!onPriceChange) return;

            if (applyTimerRef.current) {
                window.clearTimeout(
                    applyTimerRef.current
                );
            }

            if (delay === 0) {
                onPriceChange(nextMin, nextMax);
                triggerCheckAnimation();
                return;
            }

            applyTimerRef.current =
                window.setTimeout(() => {
                    applyTimerRef.current = null;

                    onPriceChange(
                        nextMin,
                        nextMax
                    );

                    triggerCheckAnimation();
                }, delay);
        },
    [applyDelay,onPriceChange,triggerCheckAnimation]);

    const updatePrice = useCallback((value: number,side: PriceSide,delay = applyDelay) => {
        let stepped = Math.round(value / PRICE_STEP) * PRICE_STEP;
        stepped = Math.max(
            PRICE_MIN,
            Math.min(PRICE_MAX, stepped)
        );

        if (side === "min") {
            if (stepped >=maxValue - PRICE_STEP) {
                stepped = maxValue - PRICE_STEP;
            }
            const nextMin = Math.max(
                PRICE_MIN,
                stepped
            );
            const nextMax = maxValue;
            setMinValue(nextMin);
            applyPrice(nextMin,nextMax,delay);

            return;
        }
        if (stepped <=minValue + PRICE_STEP) {
            stepped =  minValue + PRICE_STEP;
        }
        const nextMin = minValue;
        const nextMax = Math.min(
            PRICE_MAX,
            stepped
        );
        setMaxValue(nextMax);
        applyPrice(nextMin,nextMax,delay);
    },[ minValue, maxValue, applyDelay, applyPrice]);

    const updatePriceFromClientX = useCallback( (clientX: number,side: PriceSide,delay = 450) => {
        const wrapper = priceWrapperRef.current;
        if (!wrapper) return;
        const rect = wrapper.getBoundingClientRect();
        const x = clientX - rect.left;
        const ratio = Math.max(
            0,
            Math.min(
                1,
                x / rect.width
            )
        );
        const value = PRICE_MIN + ratio * (PRICE_MAX - PRICE_MIN);

        updatePrice(value,side,delay);
    },[updatePrice]);

    const handleMinChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        updatePrice(
            Number(e.target.value),
            "min"
        );
    },[updatePrice]);

    const handleMaxChange = useCallback(( e: React.ChangeEvent<HTMLInputElement>) => {
        updatePrice(
            Number(e.target.value),
            "max"
        );
    },[updatePrice]);

    const handlePriceClick = useCallback((e: React.MouseEvent<HTMLDivElement>,side: PriceSide) => {
        e.preventDefault();
        e.stopPropagation();
        if (hasDraggedRef.current) {
            hasDraggedRef.current = false;
            return;
        }

        updatePriceFromClientX(
            e.clientX,
            side,
            0
        );
    },[updatePriceFromClientX]);

    const handlePriceTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
        const touch = e.touches[0];
        if (!touch) return;
        touchStartRef.current = {x: touch.clientX,y: touch.clientY};
        touchDirectionRef.current =null;
        hasDraggedRef.current = false;
    },[]);

    const handlePriceTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>,side: PriceSide) => {
        const start = touchStartRef.current;
        const touch = e.touches[0];
        if (!start || !touch) return;
        const deltaX = touch.clientX - start.x;
        const deltaY = touch.clientY - start.y;

        if (touchDirectionRef.current ===  null &&Math.abs(deltaX) < 6 &&Math.abs(deltaY) < 6) return;
        if ( touchDirectionRef.current === null) touchDirectionRef.current = Math.abs(deltaX) > Math.abs(deltaY) ? "horizontal" : "vertical";
        if ( touchDirectionRef.current === "vertical") return;
        hasDraggedRef.current = true;
        e.preventDefault();
        e.stopPropagation();

        updatePriceFromClientX(touch.clientX,side,450);
    },[updatePriceFromClientX]);

    const handlePriceTouchEnd = useCallback(() => {
        touchStartRef.current = null;
        touchDirectionRef.current = null;
    }, []);

    const resetPrice = useCallback((apply = false) => {
        setMinValue(PRICE_MIN);
        setMaxValue(PRICE_MAX);

        if (apply) {
            applyPrice(
                PRICE_MIN,
                PRICE_MAX,
                0
            );
        }
    },[applyPrice]);

    useEffect(() => {
        return () => {
            if (applyTimerRef.current) {
                window.clearTimeout(applyTimerRef.current);
            }

            if (checkTimerRef.current) {
                window.clearTimeout(checkTimerRef.current);
            }
        };
    }, []);


    return {minValue,maxValue,middle,showCheck,priceWrapperRef,setMinValue,setMaxValue,resetPrice,handleMinChange,handleMaxChange,handlePriceClick,handlePriceTouchStart,handlePriceTouchMove,handlePriceTouchEnd
    };
};